 
import { useStore } from '../store';
import { fmtMoney } from '../bridge';

/** Guest/zoo business research (Office > Guest business): what completed
 *  studies earn the guest side, aggregated over the research tree's effect
 *  keys registered by Sim-Core #401 (lodging_rate_bonus_bp,
 *  tour_demand_bonus_bp, ticket_price_tolerance_bp, zoo_satisfaction_gain).
 *  Effects are summed client-side from researchTree nodes - the sim projects
 *  per-study effects, not a roll-up. The consumers (the bonus actually
 *  moving lodging income, tour demand and gate demand) land with Mgmt-Sim
 *  and the zoo lane; until then these are registry values, flagged. */

interface GuestEffects {
  lodging_rate_bonus_bp?: number; tour_demand_bonus_bp?: number;
  ticket_price_tolerance_bp?: number; zoo_satisfaction_gain?: number;
}
const GUEST_KEYS = ['lodging_rate_bonus_bp', 'tour_demand_bonus_bp', 'ticket_price_tolerance_bp', 'zoo_satisfaction_gain'] as const;
type GuestKey = (typeof GUEST_KEYS)[number];

const pct = (bp: number): string => `${(bp / 100).toFixed(bp % 100 === 0 ? 0 : 1)}%`;

const ROWS: Array<{ key: GuestKey; label: string; describe: (v: number) => string }> = [
  { key: 'lodging_rate_bonus_bp', label: 'Lodging rates', describe: (v) => `+${pct(v)} nightly lodging income` },
  { key: 'tour_demand_bonus_bp', label: 'Tour demand', describe: (v) => `+${pct(v)} tour booking demand` },
  { key: 'ticket_price_tolerance_bp', label: 'Ticket tolerance - not applied', describe: (v) => `${pct(v)} recorded; no gate-price effect is applied` },
  { key: 'zoo_satisfaction_gain', label: 'Guest satisfaction - not applied', describe: (v) => `${String(v)} recorded; no satisfaction gain is applied` },
];

function effectsOf(node: { effects: unknown }): GuestEffects {
  return (node.effects ?? {});
}
const hasGuestKey = (node: { effects: unknown }): boolean => GUEST_KEYS.some((k) => (effectsOf(node)[k] ?? 0) !== 0);
const fmtEffect = (key: GuestKey, v: number): string => (key === 'zoo_satisfaction_gain' ? `+${String(v)} satisfaction` : `+${pct(v)}`);

export function GuestResearchPanel() {
  const { sim, openPanel } = useStore();
  const tree = sim.researchTree;
  if (!tree) {
    return (
      <div style="padding:6px 14px 16px">
        <div class="eyebrow eyebrow-rule">Guest business</div>
        <p style="font-size:13px;color:var(--ui-ink-soft);margin-top:8px">
          Guest-business research bonuses appear once the research tree projection lands.
        </p>
      </div>
    );
  }

  const totals: Record<GuestKey, number> = { lodging_rate_bonus_bp: 0, tour_demand_bonus_bp: 0, ticket_price_tolerance_bp: 0, zoo_satisfaction_gain: 0 };
  for (const node of tree.tree) {
    if (node.state !== 'completed') continue;
    const fx = effectsOf(node);
    for (const k of GUEST_KEYS) totals[k] += fx[k] ?? 0;
  }
  const pipeline = tree.tree.filter((n) => n.state !== 'completed' && hasGuestKey(n));
  const anyEarned = GUEST_KEYS.some((k) => totals[k] !== 0);

  return (
    <div style="padding:6px 14px 16px">
      <div class="eyebrow eyebrow-rule">Guest business - research</div>

      <div class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:10px;padding:10px 12px">
        <div class="row-label">Recorded from completed studies</div>
        {!anyEarned && (
          <p style="font-size:13px;color:var(--ui-ink-soft);margin:6px 0 0">No completed guest effects recorded yet. Lodging and tour effects have consumers; gate and satisfaction effects are recorded only.</p>
        )}
        {ROWS.map(({ key, label, describe }) => totals[key] !== 0 && (
          <div key={key} class="row" style="margin:0;border:none;padding:6px 0">
            <span class="row-main">
              <div class="row-value" style="font-size:13px">{label}</div>
              <div class="row-label" style="text-transform:none;letter-spacing:0.01em;white-space:normal">{describe(totals[key])}</div>
            </span>
          </div>
        ))}
      </div>

      {pipeline.length > 0 && <div class="eyebrow" style="margin-top:14px">In the pipeline</div>}
      {pipeline.map((n) => {
        const fx = effectsOf(n);
        const parts = GUEST_KEYS.filter((k) => (fx[k] ?? 0) !== 0).map((k) => `${ROWS.find(row => row.key === k)?.label ?? k}: ${fmtEffect(k, fx[k] ?? 0)}`);
        return (
          <div key={n.id} class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:8px;padding:10px 12px">
            <div class="row" style="margin:0;border:none;padding:0">
              <span class="row-main">
                <div class="row-value" style="font-size:14px">{n.name}</div>
                <div class="row-label" style="text-transform:none;letter-spacing:0.01em;white-space:normal">{parts.join(' - ')} - ${fmtMoney(n.costCents)} - {n.durationDays}d</div>
              </span>
              {n.state === 'active' && <span class="pill pill-amber">running{tree.active && tree.active.studyId === n.id ? ` - ${String(tree.active.remainingDays)}d left` : ''}</span>}
              {n.state === 'locked' && <span class="pill">needs {n.missingPrereqs.length} more</span>}
            </div>
          </div>
        );
      })}

      <div class="row-label" style="margin-top:12px;text-transform:none;letter-spacing:0.01em;white-space:normal">
        Completed lodging-rate studies affect modeled nightly lodging billing; tour-demand studies affect modeled demand before vehicle capacity and integer rounding. These are not guaranteed cash or guest increases. Gate-price tolerance and satisfaction values are recorded but not applied by the current game. Values remain existing design defaults, not newly chosen rates.
      </div>
      <button class="btn btn-quiet" style="width:100%;margin-top:10px" onClick={() => { openPanel({ kind: 'office', section: 'research' }); }}>Open research</button>
    </div>
  );
}
